import { Flex, Progress, Text, useDisclosure, useToast } from '@chakra-ui/react'
import {
  ArrowUpTrayIcon,
  Button,
  Dialog,
  FileDrop,
  Menu,
  Stepper,
  StepperStep,
  TextField,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { isEmpty, isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import {
  IUserSession,
  PrimaryLeadProperties,
  PrimaryLeadProperty,
  SessionLead,
  primaryLeadProperties,
} from 'types'
import {
  BatchCreateRow,
  BatchUpdateRow,
  acceptedSpreadsheets,
  batchWrite,
  usePermission,
  useScrolling,
  useShowDevelopmentFeatures,
} from 'utils'
import { read, utils } from 'xlsx'
import { useEvent } from '../../utils/eventUtils'
import PropertyMapper, { defaultPrimaryProperties } from '../PropertyMapper'

export default function SessionImportButton() {
  const toast = useToast()
  const { overflowRef, contentRef, scrolling } = useScrolling()
  const user = useCurrentUser()
  const [showDevelopmentFeatures] = useShowDevelopmentFeatures()
  const permission = usePermission()
  const { event, sessions, status } = useEvent()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [stepIndex, setStepIndex] = useState(0)
  const [appTitle, setAppTitle] = useState('')
  const [spreadsheet, setSpreadsheet] = useState<{ file: File; buffer: any }>()
  const [selectedTab, setSelectedTab] = useState<string>('')
  const [data, setData] = useState<Record<string, string>[]>()
  const [propertyMap, setPropertyMap] = useState<PropertyMap>()
  const [progress, setProgress] = useState(0)

  const { tabNames, worksheets } = useMemo(() => {
    if (!spreadsheet?.buffer) return { tabNames: [], worksheets: {} }

    const { SheetNames, Sheets } = read(spreadsheet.buffer)
    return { tabNames: SheetNames, worksheets: Sheets }
  }, [spreadsheet])

  if (!selectedTab && tabNames.length === 1) setSelectedTab(tabNames[0])

  const fileName = spreadsheet?.file.name
  const worksheet = worksheets[selectedTab]
  const isManager = user?.id === event?.managerId
  const firstRow = data?.[0]
  const changes = makeChanges(propertyMap, data, sessions)
  const enableStep2 = Boolean(appTitle && selectedTab)
  const enableStep3 = !isEmpty(propertyMap)
  const steps: StepperStep[] = [
    { title: 'Upload', description: 'Upload File', canClick: true },
    { title: 'Mapping', description: 'Map Columns', canClick: enableStep2 },
    { title: 'Review', description: 'Finalize Process', canClick: enableStep3 },
  ]

  useEffect(() => {
    if (!worksheet) return
    setData(utils.sheet_to_json(worksheet) as Record<string, string>[])
  }, [worksheet])

  if (!showDevelopmentFeatures || (permission.event !== 'admin' && !isManager)) return null

  async function handleSpreadsheet(files: File[]) {
    try {
      if (!files?.length) throw 'File type not accepted'

      const file = files[0]
      const buffer = await file.arrayBuffer()
      if (!buffer) throw 'File type not accepted'

      setSelectedTab('')
      setData(undefined)
      setSpreadsheet({ file, buffer })
    } catch (error: any) {
      console.error(error)
      toast({ title: error.message, status: 'error', isClosable: true })
    }
  }

  async function handleUpload() {
    const batchRows = [
      ...changes.updated.map(
        session =>
          ({
            type: 'update',
            body: { ...session, title: appTitle },
          } as BatchUpdateRow<'userSessions'>)
      ),
      ...changes.new.map(
        session =>
          ({
            type: 'create',
            body: {
              app: 'custom',
              title: appTitle,
              eventId: event?.id,
              workspaceId: event?.workspaceId,
              ...session,
            },
          } as BatchCreateRow<'userSessions'>)
      ),
    ]

    try {
      await batchWrite(user!.id, 'userSessions', batchRows, setProgress)
      handleDone()
    } catch (error) {
      console.error(error)
      setProgress(0)
      toast({ title: 'Error uploading file', status: 'error' })
    }
  }

  function handleDone() {
    setStepIndex(0)
    setAppTitle('')
    setSelectedTab('')
    setSpreadsheet(undefined)
    setPropertyMap({})
    setData(undefined)
    setProgress(0)
    onClose()
  }

  return (
    <>
      <Button
        isDisabled={status !== 'success'}
        leftIcon={<ArrowUpTrayIcon />}
        onClick={onOpen}
        borderRadius="sm"
      >
        Import
      </Button>

      <Dialog
        size="xl"
        title="Import Sessions"
        bodyProps={{ gap: 6, display: 'flex', flexDirection: 'column', py: 6, overflow: 'hidden' }}
        isOpen={isOpen}
        onClose={handleDone}
      >
        <Stepper px={6} index={stepIndex} steps={steps} onChange={setStepIndex} />

        <Flex
          ref={overflowRef}
          flexDirection="column"
          overflow="auto"
          borderY={scrolling ? '1px' : 'none'}
          borderColor="gray.300"
        >
          {stepIndex === 0 ? (
            <Flex flexDirection="column" gap={2} px={6}>
              <TextField
                label="App Title"
                value={appTitle}
                borderRadius="sm"
                onChange={e => setAppTitle(e.target.value)}
                helperText="The name of the application associated with the leads"
              />
              <FileDrop onDrop={handleSpreadsheet} options={{ accept: acceptedSpreadsheets }}>
                {({ open }) => (
                  <Flex
                    p={3}
                    border={`1px ${fileName ? 'solid' : 'dashed'} gray`}
                    align="center"
                    justifyContent="center"
                    borderRadius="sm"
                    gap={2}
                  >
                    <Button onClick={open} borderRadius="sm" palette="primary" size="sm">
                      {tabNames?.length ? 'Replace File' : 'Upload File'}
                    </Button>
                    <Text textStyle="bodySmall" fontWeight="normal" color="gray.500">
                      {fileName || 'or drop a file here'}
                    </Text>
                  </Flex>
                )}
              </FileDrop>
              <Flex gap={2}>
                <Menu
                  alignSelf="flex-start"
                  isDisabled={!tabNames?.length}
                  label="Tab Name"
                  borderRadius="sm"
                  noPortal
                  onChange={setSelectedTab}
                  options={tabNames.map(value => ({ value }))}
                  value={selectedTab}
                  variant="outline"
                />
                {data?.length ? (
                  <Text textStyle="bodySmall" color="gray.500" paddingTop="36px">
                    {data.length} rows
                  </Text>
                ) : null}
              </Flex>
            </Flex>
          ) : stepIndex === 1 ? (
            <PropertyMapper
              _ref={contentRef}
              properties={['created', ...primaryLeadProperties]}
              defaultProperties={['created', ...defaultPrimaryProperties]}
              inputMap={firstRow!}
              onChange={setPropertyMap}
            />
          ) : (
            <Flex flexDirection="column" gap={4} px={6}>
              <ReviewItem label="App Name" value={appTitle} />
              <ReviewItem label="File Name" value={fileName || ''} />
              <ReviewItem label="Tab Name" value={selectedTab} />
              <ReviewItem
                label="Row Count"
                value={`${data?.length.toString() || '0'} rows (${changes.new.length} new, ${
                  changes.updated.length
                } updated)`}
              />
              <Progress
                background={progress ? 'gray.300' : 'transparent'}
                borderRadius="999px"
                colorScheme="secondary"
                sx={{ '& > div': { transition: 'width 100ms ease' } }}
                value={progress}
              />
            </Flex>
          )}
        </Flex>

        <Flex justifyContent="space-between" px={6}>
          <Button
            variant="outline"
            isDisabled={Boolean(progress)}
            onClick={() => {
              if (stepIndex > 0) setStepIndex(stepIndex - 1)
              else handleDone()
            }}
          >
            {stepIndex > 0 ? 'Back' : 'Cancel'}
          </Button>
          <Button
            palette="primary"
            isDisabled={
              stepIndex === 0 ? !enableStep2 : stepIndex === 1 ? !enableStep3 : Boolean(progress)
            }
            onClick={() => {
              if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1)
              else handleUpload()
            }}
          >
            {stepIndex === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Flex>
      </Dialog>
    </>
  )
}

interface PropertyMap extends PrimaryLeadProperties {
  created?: string
}

function ReviewItem({ label, value }: { label: string; value: string }) {
  return (
    <Flex flexDirection="column" gap={2}>
      <Text textStyle="bodySmall" color="gray.500">
        {label}
      </Text>
      <Text textStyle="bodyMedium">{value}</Text>
    </Flex>
  )
}

interface Changes {
  new: Partial<IUserSession>[]
  updated: IUserSession[]
}

function makeChanges(
  propertyMap?: PropertyMap,
  data?: Record<string, any>[],
  sessions?: IUserSession[]
) {
  const empty: Changes = { new: [], updated: [] }
  if (!propertyMap || !data?.length) return empty

  const propertyKeys = Object.keys(propertyMap).filter(
    p => p !== 'created'
  ) as PrimaryLeadProperty[]
  const createdKey = propertyMap.created

  return data.reduce<Changes>((acc, row) => {
    const lead = Object.keys(row).reduce((acc, key) => {
      const mappedKey = propertyKeys.find(
        mappedFieldKey => propertyMap[mappedFieldKey as PrimaryLeadProperty] === key
      ) as PrimaryLeadProperty | undefined

      if (mappedKey) acc[mappedKey] = row[key]
      return acc
    }, {} as SessionLead)

    const base = { ...(createdKey && { created: dateFromExcel(row[createdKey]) }) }
    const emailField = propertyMap?.email
    if (emailField) {
      const existingSession = sessions?.find(session => session.lead?.email === row[emailField])
      if (existingSession) {
        const updatedLead = { ...existingSession.lead, ...lead }
        const updatedSession = { ...existingSession, ...base, lead: updatedLead }
        if (isEqual(updatedSession, existingSession)) return acc

        acc.updated.push(updatedSession)
        return acc
      }
    }

    acc.new.push({ ...base, lead })
    return acc
  }, empty)
}

function dateFromExcel(excelDate: any) {
  switch (typeof excelDate) {
    case 'string':
      return new Date(excelDate).getTime()
    case 'number':
      return new Date(Date.UTC(0, 0, excelDate - 1)).getTime()
    default:
      throw new Error('Invalid date format')
  }
}
